/**
 * Saga
 */
import { channel } from 'redux-saga';
import { delay, put, takeEvery, call, select } from 'redux-saga/effects';

/**
 * Library
 */
import { AxiosResponse } from 'axios';
import qs from 'qs';

/**
 * Actions
 */
import {
  RequestAssetAction,
  RequestAssetDownloadAction,
  RequestAssetDownloadS3FromWebServerAction,
  RequestAssetListAction,
  RequestInterviewDownloadCloudfrontAction,
  RequestHighlightDownloadCloudfrontAction,
} from 'redux/actions/asset/type';
import {
  RequestAssetDownloadS3Action,
  RequestAssetDownloadCloudfrontAction,
} from 'redux/actions/asset/type';
import {
  requestAssetListSuccess,
  requestAssetListFailure,
  requestAssetFailure,
  requestAssetSuccess,
  requestAssetDownloadFailure,
  requestAssetDownloadSuccess,
  requestAssetDownloadS3Success,
  requestAssetDownloadCloudfrontSuccess,
  selectQuarter,
  setQuarterList,
} from 'redux/actions/asset/actions';
import { requestAssetDownloadS3Failure } from 'redux/actions/asset/actions';
import {
  addDownloadingGroup,
  removeDownloadingGroup,
  updateDownloadingGroup,
} from 'redux/actions/actions';
import {
  REQUEST_ASSET,
  REQUEST_ASSET_DOWNLOAD,
  REQUEST_ASSET_DOWNLOAD_CLOUDFRONT,
  REQUEST_ASSET_DOWNLOAD_S3,
  REQUEST_ASSET_LIST,
  REQUEST_HIGHLIGHT_DOWNLOAD_CLOUDFRONT,
  REQUEST_INTERVIEW_DOWNLOAD_CLOUDFRONT,
} from 'redux/actions/asset/types';

import { throwError, requestScriptList } from 'redux/actions/actions';

/**
 * Framework
 */
import { getAPI } from 'commons/framework/KeyFlowAPIRequest';
import KeyFlowResponse from 'commons/framework/KeyFlowResponse';
import HostFactory from 'commons/handler/HostFactory';

/**
 * constants
 */
import { API } from 'commons/constants/KeyFlowAPI';
import { Auth, MoveToDownloadPageDelay } from 'commons/constants/KeyFlowConstants';
import { API_URL, SearchCondition } from 'commons/constants/KeyFlowConstants';

/**
 * VO
 */
import AssetVO from 'commons/vo/AssetVO';
import StorageVO from 'commons/vo/StorageVO';
import CategoryVO from 'commons/vo/CategoryVO';
import QuarterVO from 'commons/vo/QuarterVO';
import TeamVO from 'commons/vo/TeamVO';
import AssetParamVO from 'commons/vo/param/AssetParamVO';
import ScriptParamVO from 'commons/vo/param/ScriptParamVO';

/**
 * Utils
 */
import { encodeSpecialCharacter } from 'commons/utils/commonUtil';
import TimeCode from 'commons/utils/TimeCode';
import ATagDownloadUtils from 'commons/utils/ATagDownloadUtils';

import { PreviewParamProps } from 'pages/type';
import S3AccessURL from 'commons/vo/S3AccessURL';
import { LowresRelativeURLBuilder } from 'commons/utils/MediaURLBuilder';
import { preflightRequest } from 'commons/utils/Preflight';
import { requestStatisticsLog } from 'redux/actions/statisticsLog/actions';
import StatisticsLogVO from 'commons/vo/param/StatisticsLogVO';

function secondToFrame({ fps, time, type }: { fps: number; time?: number; type: boolean }) {
  if (time) {
    const timecodeUtil = new TimeCode(fps);
    const _time = type ? Math.ceil(time) : Math.floor(time);
    timecodeUtil.setSeconds(_time);
    return timecodeUtil.getFrames();
  }

  return undefined;
}

function* requestAssetSaga(action: RequestAssetAction) {
  try {
    const { params } = action.payload;

    console.log(params);

    const { assetId, in: inTime, out: outTime, keyword, subType }: PreviewParamProps = params;
    const assetParam = new AssetParamVO({ id: assetId });

    const assetResponse: AxiosResponse = yield call(getAPI, API.ASSET, assetParam);
    const assetKeyFlowResponse = new KeyFlowResponse(assetResponse.data);
    const asset: AssetVO = assetKeyFlowResponse.getResult(AssetVO)[0] as AssetVO;
    const { team1, team2 } = asset;

    console.log({ asset });

    const teamResponse: AxiosResponse = yield call(getAPI, API.TEAM, {
      teamIdList: [team1, team2],
    });
    const teamKeyFlowResponse = new KeyFlowResponse(teamResponse.data);
    const teamList: TeamVO[] = teamKeyFlowResponse.getResult(TeamVO) as TeamVO[];

    console.log({ teamList });

    const { categoryList } = yield select(state => state.category);
    const { storageList } = yield select(state => state.storage);

    let selectedCategory: CategoryVO | null = null;
    let selectedStorage: StorageVO | null = null;

    const selectedCategoryList = categoryList.filter(
      (category: CategoryVO) => Number(asset.categoryId) === Number(category.id),
    );

    if (selectedCategoryList.length !== 0) {
      selectedCategory = selectedCategoryList[0];
    }

    const selectedStorageList = storageList.filter(
      (storage: StorageVO) => storage.id === selectedCategory?.storageId,
    );

    if (selectedStorageList.length !== 0) {
      selectedStorage = selectedStorageList[0];
    }

    if (selectedStorage) {
      asset.symbolicPath = selectedStorage.symbolicPath;
      asset.storage = selectedStorage;
    } else {
      asset.storage = null;
      asset.symbolicPath = '/Storage1';
    }

    yield put(
      requestAssetSuccess({
        asset,
        quarterList: [],
        teamList,
        selectedQuarter: null,
      }),
    );

    // 기록별 검색때 필요함
    const scriptParam = new ScriptParamVO({
      retr: 100,
      page: 0,
      sort: [SearchCondition.Sort.IN_POINT, SearchCondition.Sort.OUT_POINT],
      asc: SearchCondition.Asc.ASC,
      videoId: assetId,
      keyword,
      inPoint: secondToFrame({ fps: asset.mediaFps, time: inTime, type: false }),
      outPoint: secondToFrame({ fps: asset.mediaFps, time: outTime, type: true }),
    });
    if (keyword) {
      yield put(requestScriptList({ param: scriptParam }));
    }

    const quarterResponse: AxiosResponse = yield call(getAPI, API.WKBL_QUARTER, {
      videoId: assetId,
    });
    const quarterKeyFlowResponse = new KeyFlowResponse(quarterResponse.data);
    const quarterList: QuarterVO[] = quarterKeyFlowResponse.getResult(QuarterVO) as QuarterVO[];
    const sortedQuarterList = quarterList.sort((a, b) => a.inPoint - b.inPoint);
    const selectedQuarter: QuarterVO | null =
      sortedQuarterList.find((quarter: QuarterVO) => quarter.ref === subType) || null;

    yield put(selectQuarter({ quarter: selectedQuarter }));
    yield put(setQuarterList({ quarterList: sortedQuarterList }));
  } catch (error: any | Error) {
    console.error(error);
    yield put(throwError({ error }));
    yield put(requestAssetFailure());
  }
}

function* requestAssetDownloadS3Saga(
  action: RequestAssetDownloadS3Action,
): Generator<
  import('redux-saga/effects').CallEffect<AxiosResponse> | import('redux-saga/effects').PutEffect,
  void,
  AxiosResponse
> {
  const { id } = action.payload;
  const param = {
    assetId: id,
  };

  const downloadUtils = new ATagDownloadUtils();

  try {
    const response: AxiosResponse = yield call(getAPI, API.DOWNLOAD_URL_LOWRES, param);
    const keyFlowResponse = new KeyFlowResponse<S3AccessURL>(response.data);
    const resultList: Array<S3AccessURL> = keyFlowResponse.result;
    const result = resultList[0];

    const downloadURL = result.PRESIGNED_URL;

    downloadUtils.setHref(downloadURL).download();
    downloadUtils.close();
  } catch (error: any | Error) {
    yield put(throwError({ error }));
    yield put(requestAssetDownloadS3Failure());
  }
}

function* requestAssetDownloadCloudfrontSaga(
  action: RequestAssetDownloadCloudfrontAction,
): Generator<
  import('redux-saga/effects').CallEffect<boolean> | import('redux-saga/effects').PutEffect,
  void,
  AxiosResponse
> {
  const { categoryId, fileName, downloadFileName } = action.payload;

  const downloadUtils = new ATagDownloadUtils();

  const downloadURL = new LowresRelativeURLBuilder()
    .categoryId(categoryId)
    .fileName(fileName)
    .build();
  const url = new URL(downloadURL, HostFactory.cloudfrontURL);

  const isOk = yield call(preflightRequest, url);

  if (isOk) {
    const user = localStorage.getItem(Auth.USER_ID);
    const cleanedString = fileName.replace(/^V/, '').replace(/\.mp4$/, '');
    const assetId = parseInt(cleanedString);
    const logParam = new StatisticsLogVO({
      data: [{ ASSET_ID: assetId, TASK_TYPE: 'DOWNLOAD_FULL' }],
      user,
    });
    yield put(requestStatisticsLog({ param: logParam }));

    const timeoutId = setTimeout(() => {
      url.search = qs.stringify({ download: true, filename: downloadFileName });
      downloadUtils.setHref(url.toString()).download();
      downloadUtils.close();
    }, MoveToDownloadPageDelay);
  } else {
    yield put(
      throwError({
        error: new Error('영상이 존재하지 않거나 연결에 실패하였습니다.'),
      }),
    );
    yield put(requestAssetDownloadS3Failure());
  }
}

function* requestInterviewDownloadCloudfrontSaga(
  action: RequestInterviewDownloadCloudfrontAction,
): Generator<
  import('redux-saga/effects').CallEffect<boolean> | import('redux-saga/effects').PutEffect,
  void,
  AxiosResponse
> {
  const { downloadUrl } = action.payload;

  const downloadUtils = new ATagDownloadUtils();

  const url = new URL(downloadUrl, HostFactory.cloudfrontURL);

  const isOk = yield call(preflightRequest, url);

  if (isOk) {
    const fileNameArray = downloadUrl.split('/');

    const user = localStorage.getItem(Auth.USER_ID);
    const assetId = parseInt(fileNameArray[fileNameArray.length - 2]);

    const fileName = fileNameArray[fileNameArray.length - 1];

    const logParam = new StatisticsLogVO({
      data: [{ ASSET_ID: assetId, TASK_TYPE: 'DOWNLOAD_INTERVIEW' }],
      user,
    });
    yield put(requestStatisticsLog({ param: logParam }));

    const timeoutId = setTimeout(() => {
      url.search = qs.stringify({ download: true, filename: fileName });
      downloadUtils.setHref(url.toString()).download();
      downloadUtils.close();
    }, MoveToDownloadPageDelay);
  } else {
    yield put(
      throwError({
        error: new Error('영상이 존재하지 않거나 연결에 실패하였습니다.'),
      }),
    );
    yield put(requestAssetDownloadS3Failure());
  }
}

function* requestHighlightDownloadCloudfrontSaga(
  action: RequestHighlightDownloadCloudfrontAction,
): Generator<
  import('redux-saga/effects').CallEffect<boolean> | import('redux-saga/effects').PutEffect,
  void,
  AxiosResponse
> {
  const { downloadUrl } = action.payload;

  const downloadUtils = new ATagDownloadUtils();

  const url = new URL(downloadUrl, HostFactory.cloudfrontURL);

  const isOk = yield call(preflightRequest, url);

  if (isOk) {
    const fileNameArray = downloadUrl.split('/');

    const user = localStorage.getItem(Auth.USER_ID);
    const assetId = parseInt(fileNameArray[fileNameArray.length - 2]);

    const fileName = fileNameArray[fileNameArray.length - 1];

    const logParam = new StatisticsLogVO({
      data: [{ ASSET_ID: assetId, TASK_TYPE: 'DOWNLOAD_HIGHLIGHT' }],
      user,
    });
    yield put(requestStatisticsLog({ param: logParam }));

    const timeoutId = setTimeout(() => {
      url.search = qs.stringify({ download: true, filename: fileName });
      downloadUtils.setHref(url.toString()).download();
      downloadUtils.close();
    }, MoveToDownloadPageDelay);
  } else {
    yield put(
      throwError({
        error: new Error('영상이 존재하지 않거나 연결에 실패하였습니다.'),
      }),
    );
    yield put(requestAssetDownloadS3Failure());
  }
}

/**
 * @deprecated
 * @param action
 */
function* requestAssetDownloadWebServerToS3Saga(
  action: RequestAssetDownloadS3FromWebServerAction,
): Generator {
  const { id, title, filePath } = action.payload;
  const downloadUtils = new ATagDownloadUtils();
  const queryParams = qs.stringify({
    assetId: id,
    ACCESS_TOKEN: localStorage.getItem(Auth.ACCESS_TOKEN),
  });

  try {
    downloadUtils
      .setHref(`${HostFactory.apiURL}${API.ASSET_LOWRES_DOWNLOAD}?${queryParams}`)
      .download();
  } catch (error: any | Error) {
    yield put(throwError({ error }));
    yield put(requestAssetDownloadS3Failure());
    yield put(removeDownloadingGroup({ id }));
  } finally {
    downloadUtils.close();
    yield put(removeDownloadingGroup({ id }));
  }
}

/**
 * @deprecated
 * @param action
 */
function* requestAssetDownload(action: RequestAssetDownloadAction) {
  try {
    const { videoId, storageId, type } = action.payload;
    const response: AxiosResponse = yield call(getAPI, API.ASSET_DOWNLOAD_CHECK, {
      videoId,
      storageId,
      type,
    });
    const keyFlowResponse = new KeyFlowResponse(response.data);
    const { fileName } = keyFlowResponse.getResult()[0] as any;
    const fileFullNameList = fileName.split('.');
    const accessToken = localStorage.getItem(Auth.ACCESS_TOKEN);

    console.log(`fileName : ${fileName}`);
    const downloadUrl = `${API_URL}${
      API.ASSET_DOWNLOAD
    }?storageId=${storageId}&videoId=${videoId}&type=${type}&fileName=${encodeURIComponent(
      encodeSpecialCharacter(fileFullNameList[0]),
    )}&ACCESS_TOKEN=${accessToken}`;

    const link = document.createElement('a');
    link.id = `asset_${videoId}`;
    link.href = downloadUrl;
    document.body.appendChild(link);
    link.click();
  } catch (error: any | Error) {
    yield put(throwError({ error }));
    yield put(requestAssetDownloadFailure());
  }
}

export function* watchAssetSaga() {
  yield takeEvery(REQUEST_ASSET, requestAssetSaga);
  yield takeEvery(REQUEST_ASSET_DOWNLOAD_CLOUDFRONT, requestAssetDownloadCloudfrontSaga);
  yield takeEvery(REQUEST_INTERVIEW_DOWNLOAD_CLOUDFRONT, requestInterviewDownloadCloudfrontSaga);
  yield takeEvery(REQUEST_HIGHLIGHT_DOWNLOAD_CLOUDFRONT, requestHighlightDownloadCloudfrontSaga);
}
function dispatch(arg0: any) {
  throw new Error('Function not implemented.');
}
